import { useMutation } from '@apollo/client/react';
import { faArrowDown } from '@fortawesome/pro-light-svg-icons';
import {
  faExclamationCircle,
  faMessages,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import styled, { WebTarget } from 'styled-components';
import {
  ChatQuery,
  MarkChatReadDocument,
  SelfQuery,
} from '../../graphql/types';
import useDocumentFocus from '../../hooks/useDocumentHasFocus';
import useTracking from '../../hooks/useTracking';
import { BACKGROUND, DISABLED_OPACITY, ICON_DISABLED } from '../../theme';
import {
  EventCategories,
  EventLabels,
  EventNames,
} from '../../utils/trackingEvents';
import LoadingPanel from '../LoadingPanel';
import Button, { ButtonSize, ButtonVariant } from '../reusable/Button';
import { LinkButton } from '../reusable/Button/LinkButton';
import LoadMore, { LoadMoreSize } from '../reusable/LoadMore';
import Text, { TextColor, TextKind } from '../reusable/Text';
import { ChatSize, useChatContext } from './Context';
import { Message } from './Message';
import {
  getMessagesWithGroupingMetadata,
  getScrollableDivInChat,
} from './util';

const StyledInfiniteScrollWrapper = styled.div`
  overflow-y: hidden;
  display: flex;
  align-items: flex-end;
  width: 100%;

  // nested scroll areas to accomodate designs, see PR description https://github.com/teamexos/prince-web/pull/1656
  & .infinite-scroll-component__outerdiv {
    width: 100%;
    max-height: 100%;
    overflow-y: auto;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const StyledInfiniteScroll = styled(InfiniteScroll as unknown as WebTarget)`
  overflow-y: auto;
  display: flex;
  flex-direction: column-reverse; // see https://github.com/ankeetmaini/react-infinite-scroll-component#using-scroll-on-top

  transition: height 0.25s ease-in-out;
`;

const getChatHeight = (chatSize: ChatSize) => {
  switch (chatSize) {
    case ChatSize.Open:
      return '332px';
    case ChatSize.Maximized:
      return '605px';
    case ChatSize.Minimized:
    default:
      return '0';
  }
};

const StyledLoadMoreContainer = styled.div`
  padding: 8px;
`;

const StyledChatBanner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 24px;
  background: ${BACKGROUND};
  width: 100%;
  box-sizing: border-box;
`;

const NewMessageWrapper = styled.div`
  position: absolute;
  top: 66px;
  z-index: 3;
  display: flex;
  left: 0;
  right: 0;
  justify-content: center;
`;

const EmptyChat = () => (
  <StyledChatBanner data-testid="chat-messages-empty">
    <FontAwesomeIcon
      icon={faMessages}
      fontSize={24}
      color={ICON_DISABLED}
      opacity={DISABLED_OPACITY}
    />
    <Text kind={TextKind.BodyS} color={TextColor.Secondary}>
      This is the beginning of your conversation.
    </Text>
  </StyledChatBanner>
);

const EmptyChatPagination = () => (
  <StyledChatBanner data-testid="chat-messages-empty-pagination">
    <Text kind={TextKind.BodyS} color={TextColor.Secondary}>
      No more messages.
    </Text>
  </StyledChatBanner>
);

type LoadMoreChats = (isRetry?: boolean) => void;

const ChatError = ({ reloadChats }: { reloadChats: () => void }) => (
  <StyledChatBanner data-testid="chat-messages-error">
    <FontAwesomeIcon
      icon={faExclamationCircle}
      fontSize={24}
      color={ICON_DISABLED}
      opacity={DISABLED_OPACITY}
    />
    <Text kind={TextKind.BodyS} color={TextColor.Secondary}>
      Unable to load messages.{' '}
      <LinkButton onClick={reloadChats} testId="chat-messages-error-retry">
        Retry
      </LinkButton>
    </Text>
  </StyledChatBanner>
);

const ChatPaginationError = ({
  loadMoreChats,
}: {
  loadMoreChats: LoadMoreChats;
}) => (
  <StyledChatBanner data-testid="chat-messages-pagination-error">
    <FontAwesomeIcon
      icon={faExclamationCircle}
      fontSize={24}
      color={ICON_DISABLED}
      opacity={DISABLED_OPACITY}
    />
    <Text kind={TextKind.BodyS} color={TextColor.Secondary}>
      Unable to load old messages.{' '}
      <LinkButton
        onClick={() => loadMoreChats(true)}
        testId="chat-messages-pagination-error-retry"
      >
        Retry
      </LinkButton>
    </Text>
  </StyledChatBanner>
);

interface MessagesProps {
  chat: ChatQuery['chatRoom'];
  self: SelfQuery['self'];
  initialLoading?: boolean;
  error?: boolean;
  refetch?: () => void;
  fetchMore?: () => void;
  loadingMore?: boolean;
  paginationError?: boolean;
  scrollRef?: React.Ref<HTMLDivElement>;
  hasNewMessages?: boolean;
  setHasNewMessages?: (hasNewMessages: boolean) => void;
}

export const Messages: React.FC<MessagesProps> = ({
  self,
  initialLoading,
  error,
  refetch,
  fetchMore,
  loadingMore,
  chat,
  paginationError,
  scrollRef,
  hasNewMessages,
  setHasNewMessages,
}) => {
  const { chatSize } = useChatContext();
  const { track } = useTracking();
  const [markChatRead] = useMutation(MarkChatReadDocument);
  const { isDocumentFocused } = useDocumentFocus();

  const messages = chat?.messages?.edges.map((edge) => edge.node);
  const messagesWithMetadata = messages
    ? getMessagesWithGroupingMetadata({
        messages,
        chatUsers: chat?.users.edges,
      })
    : [];
  const chatIsEmpty = !messages || messages.length === 0;

  // mark chat read
  React.useEffect(() => {
    if (chat && messages) {
      const { id, lastMessageReceivedAt } = chat;
      if (
        isDocumentFocused &&
        chat.messages.unreadMessageCount > 0 &&
        lastMessageReceivedAt
      ) {
        const lastMessageRead = messages[messages.length - 1].createdAt;
        markChatRead({
          variables: {
            chatId: id,
            lastMessageRead,
          },
          optimisticResponse: {
            markChatRead: {
              ...chat,
              messages: { ...chat.messages, unreadMessageCount: 0 },
              lastMessageRead,
            },
          },
          refetchQueries: ['membersListTotals'],
        });
      }
    }
  }, [chat, isDocumentFocused, markChatRead, messages]);

  const scrollableDiv = getScrollableDivInChat(
    scrollRef as React.RefObject<HTMLDivElement>,
  );

  // Needs to be a callback in order to support using it in useEffect below as a dependency
  const hideNewMessagesIfScrolledToBottom = React.useCallback(() => {
    if (
      hasNewMessages &&
      // Oddly, sometimes scrollTop gets stuck at 0.5 instead of 0;
      // And when chat is scrolled up, scrollTop a negative number
      // This ensures we have a bit of a buffer for hiding new message button
      // and that we don't hide it for larger negative numbers
      scrollableDiv?.scrollTop > -1 &&
      scrollableDiv?.scrollTop < 1 &&
      setHasNewMessages
    ) {
      setHasNewMessages(false);
    }
  }, [hasNewMessages, setHasNewMessages, scrollableDiv]);

  // Resets new messages when
  React.useEffect(() => {
    hideNewMessagesIfScrolledToBottom();
  }, [hideNewMessagesIfScrolledToBottom]);

  const reloadChats = () => {
    if (refetch) {
      refetch();

      track(EventNames.RetryClick, {
        event_category: EventCategories.Chat,
        event_label: EventLabels.UnableToLoadMessages,
        chat_id: chat?.id,
      });
    }
  };

  const canLoadMore = Boolean(
    !initialLoading && !loadingMore && chat?.messages?.pageInfo.hasNextPage,
  );

  const loadMoreChats: LoadMoreChats = (isRetry?: boolean) => {
    /**
     * re: missing coverage https://exos.slack.com/archives/C044ULRB65T/p1690914314514219
     * Pagination as a whole is tested in E2E, but the mixpanel event couldn't be tested
     */
    if (fetchMore && canLoadMore) {
      fetchMore();

      if (isRetry) {
        track(EventNames.RetryClick, {
          event_category: EventCategories.Chat,
          event_label: EventLabels.UnableToLoadOldMessages,
          chat_id: chat?.id,
        });
      } else {
        track(EventNames.LoadMoreData, {
          event_category: EventCategories.Chat,
          chat_id: chat?.id,
        });
      }
    }
  };

  if (initialLoading) {
    return <LoadingPanel noBorder fullHeight testId="chat-messages-loading" />;
  }

  if (!loadingMore && !chat?.messages?.pageInfo.endCursor && error) {
    return <ChatError reloadChats={reloadChats} />;
  }

  if (chatIsEmpty && !loadingMore && !error) {
    return <EmptyChat />;
  }

  const scrollToBottom = () => {
    scrollableDiv?.scrollTo({ top: 0, behavior: 'smooth' });
    track(EventNames.NewMessageButtonClick, {
      event_category: EventCategories.Chat,
      chat_id: chat?.id,
    });
  };

  /**
   * GOTCHA!
   *
   * Warning! Adjusting any styles below this point has a high chance of breaking
   * chat pagination and/or scrolling logic. The library is very sensitive to changes
   * in styling. Anything affecting the layout is high risk.
   */
  return (
    <StyledInfiniteScrollWrapper ref={scrollRef}>
      <StyledInfiniteScroll
        dataLength={chat?.messages?.edges.length || 0}
        next={loadMoreChats}
        height={getChatHeight(chatSize)}
        hasMore={Boolean(chat?.messages?.pageInfo.hasNextPage)}
        loader={
          !paginationError && (
            <StyledLoadMoreContainer>
              <LoadMore
                isFetchingMore={loadingMore}
                loadMore={() => loadMoreChats(false)}
                size={LoadMoreSize.Small}
                flipArrow
              />
            </StyledLoadMoreContainer>
          )
        }
        endMessage={<EmptyChatPagination />}
        initialScrollY={0}
        inverse
        onScroll={hideNewMessagesIfScrolledToBottom}
      >
        <div
          data-testid="chat-messages-container"
          /**
           * We are forced to use flex-direction: column-reverse in the parent for inverse
           * pagination, luckily we can override it here so messages aren't displayed in
           * reverse order.
           */
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          {paginationError && (
            <ChatPaginationError loadMoreChats={loadMoreChats} />
          )}
          {messagesWithMetadata?.map((message) => (
            <Message
              key={message.id}
              message={message}
              isSentMessage={message.user.id === self?.id}
            />
          ))}
        </div>
      </StyledInfiniteScroll>
      {hasNewMessages && scrollableDiv?.scrollTop !== 0 && (
        <NewMessageWrapper>
          <Button
            onClick={scrollToBottom}
            icon={<FontAwesomeIcon icon={faArrowDown} />}
            variant={ButtonVariant.Dark}
            size={ButtonSize.Small}
          >
            New Message
          </Button>
        </NewMessageWrapper>
      )}
    </StyledInfiniteScrollWrapper>
  );
};
